import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowLeft, MessageSquare, AlertCircle } from 'lucide-react';
import { useCart } from '../context/CartContext';

const Cart = () => {
  const { cart, addons, updateQuantity, removeFromCart, getCartTotal, updateAddonQuantity, removeAddon } = useCart();
  const navigate = useNavigate();

  const calculateItemTotal = (item) => {
    const price = item.finalPrice || item.price || 0;
    return price * item.quantity;
  };

  const calculateAddonTotal = (addon) => {
    return addon.price * addon.quantity;
  };

  const subtotal = getCartTotal();
  const shipping = subtotal > 999 ? 0 : 99;
  const finalTotal = subtotal + shipping;

  if (cart.length === 0 && addons.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-pink-50 flex items-center justify-center py-8 px-4">
        <div className="text-center max-w-sm">
          <ShoppingBag className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-3 text-gray-900">Your cart is empty</h2>
          <p className="text-gray-600 text-sm mb-6">Add some beautiful products to your cart!</p>
          <Link 
            to="/shop" 
            className="inline-flex items-center justify-center bg-pink-600 text-white px-6 py-3 rounded-full font-medium hover:bg-pink-700"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-pink-50 py-4">
      <div className="container mx-auto px-3">
        {/* Header */}
        <div className="mb-6">
          <Link 
            to="/shop" 
            className="inline-flex items-center text-pink-600 hover:text-pink-700 text-sm font-medium"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            Continue Shopping
          </Link>
        </div>

        <h1 className="text-xl font-bold text-gray-900 mb-6">Shopping Cart</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {/* Main Products */}
            {cart.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-lg font-bold text-gray-900">Products ({cart.reduce((sum, item) => sum + item.quantity, 0)})</h2>
                {cart.map((item) => (
                  <div key={`${item.type}-${item.id}`} className="bg-white rounded-xl shadow-sm p-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                      {/* Image */}
                      <div className="sm:w-32 sm:h-32 w-full h-48">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      </div>

                      {/* Details */}
                      <div className="flex-1">
                        <div className="flex flex-col sm:flex-row justify-between">
                          <div className="flex-1">
                            <h3 className="font-bold text-gray-900 text-sm">{item.name}</h3>
                            <p className="text-gray-600 text-xs mt-1">
                              {item.type === 'combo' ? 'Special Combo' : 'Single Product'}
                            </p>

                            {/* Selected Option */}
                            {item.selectedOption && (
                              <p className="text-pink-600 text-xs mt-2">
                                Customization: {Object.keys(item.selectedOption)[0]} (+₹{item.selectedOption[Object.keys(item.selectedOption)[0]]})
                              </p>
                            )}

                            {/* Custom Message */}
        {item.customMessage && (
          <div className="mt-2 p-2 bg-blue-50 border border-blue-100 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <MessageSquare className="h-3 w-3 text-blue-500" />
              <span className="text-xs font-medium text-blue-700">Custom Message:</span>
            </div>
            <p className="text-xs text-blue-600 italic">"{item.customMessage}"</p>
          </div>
        )}


        {item.allowCustomMessage && !item.customMessage && (
          <div className="mt-2 p-2 bg-yellow-50 border border-yellow-100 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-3 w-3 text-yellow-500" />
              <span className="text-xs text-yellow-700">Custom message can be added at checkout</span>
            </div>
          </div>
        )}



                            {/* Price */}
                            <div className="mt-3">
                              <p className="text-pink-600 font-bold text-sm">
                                ₹{item.finalPrice || item.price} × {item.quantity} = ₹{calculateItemTotal(item)}
                              </p>
                              {item.originalPrice && (
                                <p className="text-gray-400 text-xs line-through">
                                  Original: ₹{item.originalPrice}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex flex-col items-end mt-4 sm:mt-0">
                            <button
                              onClick={() => removeFromCart(item.id, item.type)}
                              className="text-red-500 hover:text-red-700 mb-3"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>

                            <div className="flex items-center border rounded-lg">
                              <button
                                onClick={() => updateQuantity(item.id, item.type, item.quantity - 1)}
                                className="px-2 py-1 hover:bg-gray-100"
                              >
                                <Minus className="h-3 w-3" />
                              </button>
                              <span className="px-3 py-1 text-xs">{item.quantity}</span>
                              <button
                                onClick={() => updateQuantity(item.id, item.type, item.quantity + 1)}
                                className="px-2 py-1 hover:bg-gray-100"
                              >
                                <Plus className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Addons */}
            {addons.length > 0 && (
              <div className="mt-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Add-ons ({addons.reduce((sum, a) => sum + a.quantity, 0)})</h2>
                <div className="space-y-3">
                  {addons.map((addon) => (
                    <div key={addon.id} className="bg-white rounded-xl shadow-sm p-4">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                          <img
                            src={addon.image}
                            alt={addon.name}
                            className="w-12 h-12 object-cover rounded"
                          />
                          <div>
                            <h4 className="font-medium text-sm">{addon.name}</h4>
                            <p className="text-gray-600 text-xs">{addon.description}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          {/* Quantity Controls */}
                          <div className="flex items-center border rounded-lg">
                            <button
                              onClick={() => updateAddonQuantity(addon.id, addon.quantity - 1)}
                              className="px-2 py-1 hover:bg-gray-100"
                            >
                              <Minus className="h-3 w-3" />
                            </button>
                            <span className="px-3 py-1 text-xs">{addon.quantity}</span>
                            <button
                              onClick={() => updateAddonQuantity(addon.id, addon.quantity + 1)}
                              className="px-2 py-1 hover:bg-gray-100"
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>

                          {/* Price */}
                          <div className="text-right">
                            <p className="text-pink-600 font-bold text-sm">
                              ₹{addon.price} × {addon.quantity} = ₹{calculateAddonTotal(addon)}
                            </p>
                          </div>

                          {/* Remove */}
                          <button
                            onClick={() => removeAddon(addon.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm p-6 sticky top-4">
              <h2 className="text-lg font-bold mb-6">Order Summary</h2>
              
              {/* Price Breakdown */}
              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="text-sm">Subtotal</span>
                  <span className="text-sm">₹{subtotal}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">Shipping</span>
                  <span className="text-sm">{shipping === 0 ? 'FREE' : `₹${shipping}`}</span>
                </div>
                <div className="border-t pt-3 flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span className="text-pink-600">₹{finalTotal}</span>
                </div>
              </div>

              {/* Proceed to Checkout */}
              <button
                onClick={() => navigate('/checkout')}
                className="w-full bg-pink-600 text-white py-3 rounded-lg font-semibold hover:bg-pink-700 transition-colors mb-3"
              >
                Proceed to Checkout
              </button>

              {/* Continue Shopping */}
              <Link to="/shop">
                <button className="w-full border border-gray-300 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors">
                  Continue Shopping
                </button>
              </Link>

              {/* Notes */}
              <div className="mt-6 space-y-2 text-xs text-gray-500">
                <p>✓ Free shipping on orders above ₹999</p>
                <p>✓ Same-day delivery available</p>
                <p>✓ Custom messages editable at checkout</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;